// src/lib/mockApi.ts
/**
 * Mock API helpers (localStorage-backed)
 * -----------------------------------------------------------------------------
 * Exports:
 *   - fetchSites(): Promise<Site[]>
 *   - fetchLoads(): Promise<any[]>
 *   - addLoads(rows, target?): Promise<number>  // target: 'data' | 'inbox' (default 'data')
 *   - upsertLoad(row): Promise<void>
 *
 * Notes:
 * • Keeps previously-added Sites logic and ensures required `Site.geofence`.
 * • Loads are intentionally typed as `any` to avoid tight coupling to page-specific shapes.
 */

import { get, set } from "@/lib/storage";
import type { Site } from "@/lib/types";

const SK = {
  sites: "sites:data:v1",
  loadsData: "loads:data:v1",
  loadsInbox: "loads:inbox:v1",
} as const;

/** -------------------- Sites -------------------- */

const EMPTY_GEOFENCE: any = {
  type: "Polygon",
  coordinates: [],
  source: "mock",
};

const seedSites: Site[] = [
  {
    id: "site-hollyhill",
    name: "Holly Hill Export Yard",
    description: "Rail spur + fumigation pad",
    balances: {
      amount: 125000,
      loads: 412,
      tons: 11240,
      profit: 48750,
      avgCostPerLoad: 275,
      avgCostPerUnit: 31.5,
    },
    categories: ['Pine Log 39-10"', 'Pine Log 19-12"', 'Pine Log 16"'],
    geofence: EMPTY_GEOFENCE,
  },
  {
    id: "site-essex-west",
    name: "Essex West Block A",
    description: "Final harvest tract — sandy loam",
    balances: {
      amount: 82000,
      loads: 166,
      tons: 4520,
      profit: 21980,
      avgCostPerLoad: 295,
      avgCostPerUnit: 29.8,
    },
    categories: ["Pine CNS", "Pine Sawtimber"],
    geofence: EMPTY_GEOFENCE,
  },
  {
    id: "site-rhodes-east",
    name: "Rhodes East Thin",
    description: "Selective thin — wetter pockets",
    balances: {
      amount: 56000,
      loads: 128,
      tons: 3310,
      profit: 12400,
      avgCostPerLoad: 265,
      avgCostPerUnit: 27.3,
    },
    categories: ["Pine Pulpwood", "Pine CNS"],
    geofence: EMPTY_GEOFENCE,
  },
];

/** Fetch Sites (seeds on first run) */
export async function fetchSites(): Promise<Site[]> {
  const existing = get<Site[]>(SK.sites, []);
  if (Array.isArray(existing) && existing.length > 0) return existing;
  set(SK.sites, seedSites);
  return seedSites;
}

/** -------------------- Loads -------------------- */

/** Fetch posted loads */
export async function fetchLoads(): Promise<any[]> {
  return get<any[]>(SK.loadsData, []);
}

/**
 * Add loads to target store (posted data or inbox)
 * @param rows array of load-like objects
 * @param target 'data' (default) or 'inbox'
 * @returns number of rows added
 */
export async function addLoads(rows: any[], target: "data" | "inbox" = "data"): Promise<number> {
  const key = target === "inbox" ? SK.loadsInbox : SK.loadsData;
  const cur = get<any[]>(key, []);
  const next = [...cur, ...rows];
  set(key, next);
  return rows?.length ?? 0;
}

/**
 * Upsert a single load into posted data store keyed by `id`
 */
export async function upsertLoad(row: any): Promise<void> {
  if (!row || !row.id) {
    // generate a simple id if missing
    row = { ...row, id: "ld_" + Math.random().toString(36).slice(2) };
  }
  const cur = get<any[]>(SK.loadsData, []);
  const idx = cur.findIndex((r) => r && r.id === row.id);
  if (idx >= 0) {
    const copy = cur.slice();
    copy[idx] = { ...cur[idx], ...row };
    set(SK.loadsData, copy);
  } else {
    set(SK.loadsData, [...cur, row]);
  }
}
